import React, { useState, useEffect } from 'react'
import { FormGroup, Label, Input, Button, Row, Col } from 'reactstrap'
import Select from 'react-select'

const DEFAULT_CONDITION = { version: 'CURRENT', fieldName: '', conditionType: '', conditionValue: '' }

const buildGroupedFieldOptions = (availableModel) => {
    if (!availableModel) return []
    const directFields = (availableModel.availableModelFields || []).map((f) => ({
        label: f.replaceAll('_', ' ').toUpperCase(),
        value: f,
    }))
    const groups = [{ label: 'Direct Fields', options: directFields }]
    const related = availableModel.relatedModelFields
    if (related) {
        Object.entries(related).forEach(([rel, info]) => {
            const opts = (info.fields || []).map((f) => ({
                label: `${rel}.${f}`.replaceAll('_', ' ').toUpperCase(),
                value: `${rel}.${f}`,
            }))
            groups.push({ label: `${info.model_name} (${rel})`, options: opts })
        })
    }
    return groups
}

const ConditionGroupConfig = ({ nodeData, nodeId, availableModel, conditionTypes, triggerType, onUpdate, onDelete }) => {
    const [logicalOperator, setLogicalOperator] = useState(nodeData.logicalOperator || 'AND')
    const [conditions, setConditions] = useState(nodeData.conditions || [])

    useEffect(() => {
        setLogicalOperator(nodeData.logicalOperator || 'AND')
        setConditions(nodeData.conditions || [])
    }, [nodeData])

    const pushUpdate = (op, conds) => {
        onUpdate({ logicalOperator: op, conditions: conds })
    }

    const handleAddCondition = () => {
        const next = [...conditions, { ...DEFAULT_CONDITION, id: `temp_${Date.now()}` }]
        setConditions(next)
        pushUpdate(logicalOperator, next)
    }

    const handleRemoveCondition = (idx) => {
        const next = conditions.filter((_, i) => i !== idx)
        setConditions(next)
        pushUpdate(logicalOperator, next)
    }

    const handleConditionChange = (idx, field, value) => {
        const next = conditions.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
        setConditions(next)
        pushUpdate(logicalOperator, next)
    }

    const conditionTypeOptions = conditionTypes?.states?.map((ct) => ({
        label: ct.name.replaceAll('_', ' '),
        value: ct.name,
    })) || []

    const fieldOptions = buildGroupedFieldOptions(availableModel)

    return (
        <div className="wf-config-form">
            <div className="wf-section">
                <div className="wf-section-label">Logic</div>
                <div className="wf-operator-toggle">
                    <button
                        className={`wf-operator-btn ${logicalOperator === 'AND' ? 'wf-operator-active' : ''}`}
                        onClick={() => {
                            setLogicalOperator('AND')
                            pushUpdate('AND', conditions)
                        }}
                    >
                        AND
                    </button>
                    <button
                        className={`wf-operator-btn ${logicalOperator === 'OR' ? 'wf-operator-active' : ''}`}
                        onClick={() => {
                            setLogicalOperator('OR')
                            pushUpdate('OR', conditions)
                        }}
                    >
                        OR
                    </button>
                </div>
                <p className="wf-operator-hint">
                    {logicalOperator === 'AND'
                        ? 'All conditions must be true'
                        : 'At least one condition must be true'}
                </p>
            </div>

            <div className="wf-section">
                <div className="wf-section-label">
                    Conditions
                    <span className="wf-section-label-badge">{conditions.length}</span>
                </div>

                {conditions.length === 0 && (
                    <div className="wf-empty-state">
                        <i className="fa fa-filter wf-empty-icon" />
                        <p>No conditions yet</p>
                        <small>Add a condition to filter when this workflow runs</small>
                    </div>
                )}

                {conditions.map((cond, idx) => (
                    <div key={cond.id || idx} className="wf-condition-row">
                        <div className="wf-condition-row-header">
                            <span className="wf-condition-num">#{idx + 1}</span>
                            <button
                                className="wf-condition-remove"
                                onClick={() => handleRemoveCondition(idx)}
                                title="Remove condition"
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <Row className="mb-2">
                            <Col xs="12">
                                <Label className="wf-field-label">Version</Label>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={cond.version || 'CURRENT'}
                                    onChange={(e) => handleConditionChange(idx, 'version', e.target.value)}
                                >
                                    <option value="CURRENT">CURRENT</option>
                                    {triggerType === 'ON_UPDATE' && <option value="PREVIOUS">PREVIOUS</option>}
                                    {availableModel?.availableModelProperties?.length > 0 && (
                                        <option value="CUSTOM_FIELDS">CUSTOM FIELDS</option>
                                    )}
                                </Input>
                            </Col>
                        </Row>
                        <FormGroup className="mb-2">
                            <Label className="wf-field-label">Field</Label>
                            <Select
                                className="bos-custom-select"
                                classNamePrefix="bos-select"
                                options={
                                    cond.version === 'CUSTOM_FIELDS'
                                        ? (availableModel?.availableModelProperties || []).map((f) => ({
                                            label: f?.replaceAll('_', ' ').toUpperCase(),
                                            value: f,
                                        }))
                                        : fieldOptions
                                }
                                value={cond.fieldName ? { label: cond.fieldName.replaceAll('_', ' ').toUpperCase(), value: cond.fieldName } : null}
                                placeholder="Select field..."
                                onChange={(opt) => handleConditionChange(idx, 'fieldName', opt.value)}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </FormGroup>
                        <Row>
                            <Col xs="6">
                                <FormGroup className="mb-1">
                                    <Label className="wf-field-label">Operator</Label>
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={conditionTypeOptions}
                                        value={cond.conditionType ? { label: cond.conditionType.replaceAll('_', ' '), value: cond.conditionType } : null}
                                        placeholder="Operator..."
                                        onChange={(opt) => handleConditionChange(idx, 'conditionType', opt.value)}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs="6">
                                <FormGroup className="mb-1">
                                    <Label className="wf-field-label">Value</Label>
                                    <Input
                                        bsSize="sm"
                                        type="text"
                                        value={cond.conditionValue || ''}
                                        placeholder="Value..."
                                        onChange={(e) => handleConditionChange(idx, 'conditionValue', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                ))}

                <button className="wf-add-condition-btn" onClick={handleAddCondition}>
                    <i className="fa fa-plus" /> Add Condition
                </button>
            </div>

            <div className="wf-section wf-section-danger">
                <button className="wf-delete-btn" onClick={() => onDelete(nodeId)}>
                    <i className="fa fa-trash" /> Remove Condition Group
                </button>
            </div>
        </div>
    )
}

export default ConditionGroupConfig
